import { Box, Stack } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const VIDEO_CONTAINER_HEIGHT = 80;
export const PLAYBACK_CONTROL_HEIGHT = VIDEO_CONTAINER_HEIGHT * 0.125 - 2;
const BORDER_RADIUS = 18;

export const VideoContainer = styled(Box)<{ bgcolor?: string }>(({ theme, bgcolor }) => ({
  position: "relative",
  width: "100%",
  height: theme.spacing(VIDEO_CONTAINER_HEIGHT),
  overflow: "hidden",
  backgroundColor: bgcolor || theme.palette.background.default,
  backgroundImage: `linear-gradient(color-mix(in srgb, ${theme.palette.dsSurface.v300} 30%, transparent), color-mix(in srgb, ${theme.palette.dsSurface.v300} 30%, transparent)), url(https://challenge-thumbs.b-cdn.net/byb-backgrounds/pitch_grass.png?width=1401&height=725)`,
  backgroundSize: "80.5% 100%",
  backgroundPosition: "right top",
  backgroundRepeat: "repeat",
  borderRadius: BORDER_RADIUS,
  boxShadow: bgcolor ? `0 0 0 ${theme.spacing(2)} ${bgcolor}` : "none",
  transition: "background 0.3s ease, box-shadow 0.3s ease",
  aspectRatio: "16/9",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
}));

export const ReplayActions = styled(Stack)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 5,
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: theme.spacing(1),
  pointerEvents: "none",
  "& > *": { pointerEvents: "auto" },
}));

export const ControlBar = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  flex: "1 1 auto",
  minHeight: 0,
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  margin: theme.spacing(0, 2),
  boxSizing: "border-box",
  backdropFilter: `blur(${theme.spacing(1)})`,
  backgroundColor: `${theme.palette.common.black}99`,
  borderRadius: theme.spacing(20),
  transition: "background-color 0.3s ease",
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.spacing(4.5),
  height: theme.spacing(4.5),
  transition: "background-color 0.3s ease, transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.25)",
  },
}));

export const ProgressBarContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  borderRadius: BORDER_RADIUS,
  border: "1px solid rgba(255, 255, 255, 0.2)",
  height: theme.spacing(6),
  display: "flex",
  alignItems: "center",
  padding: `0 ${theme.spacing(2.5)}`,
  transition: "background-color 0.3s ease",
}));

export const ProgressBar = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  height: theme.spacing(1),
  backgroundColor: `${theme.palette.dsSurface.v500}`,
  borderRadius: theme.spacing(2),
  position: "relative",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
}));

export const ProgressFill = styled(Stack)(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  height: "100%",
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS,
  zIndex: 3,

  "&::after": {
    content: '""',
    position: "absolute",
    right: -2,
    top: "50%",
    transform: "translateY(-50%)",
    width: theme.spacing(0.75),
    height: theme.spacing(2.5),
    backgroundColor: theme.palette.common.white,
    border: "2px solid white",
    borderRadius: theme.spacing(0.3),
    boxSizing: "border-box",
  },
}));

export const HighlightRange = styled(Box)<{
  startTime: number;
  endTime: number;
  duration: number;
}>(({ theme, startTime, endTime, duration }) => ({
  position: "absolute",
  left: `${Math.max(0, Math.min(100, (startTime / duration) * 100))}%`,
  top: 0,
  width: `${Math.max(0, Math.min(100 - (startTime / duration) * 100, ((endTime - startTime) / duration) * 100))}%`,
  height: "100%",
  backgroundColor: `${theme.palette.dsNeutral.v400}`,
  borderRadius: "inherit",
  zIndex: 1,
}));

export const ControlButtonGroup = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: 0,
  alignItems: "center",
  flexShrink: 0,
}));

export const InfoDisplayContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  flexShrink: 0,
}));

export const SettingsContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
}));

export const SettingsTogglesWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "160%",
  left: "50%",
  transform: "translateX(-50%)",
  marginBottom: theme.spacing(1),
}));

export const SettingsTogglesList = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  gap: theme.spacing(1.5),
  alignItems: "center",
}));

export const SettingsToggleButton = styled(Box)<{
  isActive: boolean;
  showSettings: boolean;
  animationIndex: number;
}>(({ theme, isActive, showSettings, animationIndex }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  cursor: "pointer",
  borderRadius: theme.spacing(3),
  backgroundColor: isActive ? theme.palette.dsPrimary.v300 : `${theme.palette.common.black}99`,
  width: theme.spacing(17),
  opacity: showSettings ? 1 : 0,
  transform: showSettings ? "translateY(0)" : "translateY(60px)",
  transition: "opacity 0.4s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease",
  transitionDelay: showSettings ? `${animationIndex * 0.1 + 0.05}s` : "0s",
  pointerEvents: showSettings ? "auto" : "none",
  color: isActive ? theme.palette.common.white : theme.palette.dsNeutral.v300,
  "& .MuiTypography-root": {
    color: "inherit",
    transition: "color 0.2s ease",
  },
}));

export const HighlightPoint = styled(Box)<{
  startTime: number;
  duration: number;
  isSelected: boolean;
  onClick: (event: React.MouseEvent) => void;
}>(({ theme, startTime, duration, isSelected }) => ({
  position: "absolute",
  left: `${Math.max(0, Math.min(100, (startTime / duration) * 100))}%`,
  top: "50%",
  transform: "translate(-50%, -17.5%)",
  zIndex: 2,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  transition: "all 0.2s ease-in-out",

  "&::before": {
    content: '""',
    display: "block",
    width: isSelected ? 2 : 1.5,
    height: theme.spacing(1),
    backgroundColor: theme.palette.dsNeutral.v300,
    transition: "all 0.2s ease-in-out",
  },

  "&::after": {
    content: '""',
    display: "block",
    width: 0,
    height: 0,
    borderLeft: `${isSelected ? 5 : 4}px solid transparent`,
    borderRight: `${isSelected ? 5 : 4}px solid transparent`,
    borderBottom: `${isSelected ? 6 : 5}px solid ${theme.palette.common.white}`,
    transition: "all 0.2s ease-in-out",
  },

  "&:hover::before": {
    width: 2,
    height: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },

  "&:hover::after": {
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: `7px solid ${theme.palette.common.white}`,
  },
}));

export const ReplayLoadingOverlay = styled(Box, {
  shouldForwardProp: prop => prop !== "isComplete",
})<{ isComplete: boolean }>(({ theme, isComplete }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 10,
  overflow: "hidden",
  pointerEvents: "none",
  borderRadius: theme.spacing(2),
  background: `
    radial-gradient(circle at 50% 45%, ${alpha(theme.palette.common.white, 0.09)} 0%, transparent 24%),
    linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.16)} 0%, ${alpha(theme.palette.common.black, 0.62)} 100%)
  `,
  backdropFilter: "blur(2px)",
}));

export const ReplayLoadingBackdrop = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  background: `radial-gradient(circle at center, transparent 0%, transparent 30%, ${alpha(theme.palette.common.black, 0.3)} 100%)`,
}));

export const ReplayLoadingContent = styled(Stack)({
  position: "absolute",
  inset: 0,
  zIndex: 2,
  pointerEvents: "none",
});

export const ReplayLoaderFrame = styled(Box)(({ theme }) => ({
  position: "relative",
  width: theme.spacing(15.25),
  height: theme.spacing(15.25),
  display: "grid",
  placeItems: "center",
  [theme.breakpoints.up("md")]: {
    width: theme.spacing(18.5),
    height: theme.spacing(18.5),
  },
}));

export const ReplayLoaderSvg = styled("svg")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  overflow: "visible",
  filter: `drop-shadow(0 0 12px ${alpha(theme.palette.dsPrimary.v300, 0.24)})`,
}));

export const ReplayLoaderLabel = styled(Stack)({
  position: "relative",
  zIndex: 1,
});

export const ReplayLoaderValue = styled(Box)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: "2rem",
  lineHeight: 1,
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  letterSpacing: "-0.04em",
  textShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.34)}`,
  [theme.breakpoints.up("md")]: {
    fontSize: "2.6rem",
  },
}));
